/**
 * Tabs for the trick-scooter rider-height picker.
 *
 * The panels are rendered server-side and all three exist in the document; this
 * only moves `hidden` and the ARIA state between them. That means the page works
 * without JavaScript — the default panel is already open and the other two are
 * simply closed, which is a smaller loss than an empty module.
 *
 * Delegated from the section root rather than bound per button, because the
 * section is re-rendered by the Section Rendering API when a filter changes and
 * per-element listeners would silently stop working after that.
 */
(function () {
  'use strict';

  var root = document.querySelector('.tsp');
  if (!root) return;

  var tabs = root.querySelectorAll('[data-tsp-tab]');
  var panels = root.querySelectorAll('[data-tsp-panel]');
  if (!tabs.length || !panels.length) return;

  function show(index) {
    for (var i = 0; i < tabs.length; i++) {
      var on = tabs[i].getAttribute('data-tsp-tab') === index;
      tabs[i].classList.toggle('tsp__tab--on', on);
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-tsp-panel') !== index;
    }
  }

  root.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-tsp-tab]');
    if (!tab) return;
    show(tab.getAttribute('data-tsp-tab'));
  });

  /* Left and right arrows move between tabs, which is what a tablist is
     expected to do and what a keyboard user will try. */
  root.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    var tab = event.target.closest('[data-tsp-tab]');
    if (!tab) return;

    var list = Array.prototype.slice.call(tabs);
    var at = list.indexOf(tab);
    var next = event.key === 'ArrowRight' ? at + 1 : at - 1;
    if (next < 0) next = list.length - 1;
    if (next >= list.length) next = 0;

    event.preventDefault();
    show(list[next].getAttribute('data-tsp-tab'));
    list[next].focus();
  });

  /* ── Reaching the products past Liquid's cap ─────────────────────────────
   *
   * Liquid hands a section at most 50 products regardless of `limit` or
   * `offset`, and this collection publishes more. Six models were absent from
   * every tab because all their colourways sat past that cap.
   *
   * So the remaining pages are fetched through the Section Rendering API and
   * merged in. The markup arrives already rendered by Liquid — including the
   * prices, which must never be formatted here, because that is where the
   * theme's EUR symbol-placement correction gets lost.
   *
   * Failing quietly is the right behaviour: the picker still shows the first
   * fifty products' models, which is most of them, and the grid below is
   * complete either way.
   */
  var total = parseInt(root.getAttribute('data-tsp-total'), 10) || 0;
  var seen = parseInt(root.getAttribute('data-tsp-seen'), 10) || 50;
  var handle = root.getAttribute('data-tsp-handle');

  function mergeInto(panelIndex, items) {
    var panel = root.querySelector('[data-tsp-panel="' + panelIndex + '"] .tsp__models');
    if (!panel) return;

    var present = {};
    var existing = panel.querySelectorAll('[data-tsp-model]');
    for (var i = 0; i < existing.length; i++) {
      present[existing[i].getAttribute('data-tsp-model')] = true;
    }

    for (var j = 0; j < items.length; j++) {
      var name = items[j].getAttribute('data-tsp-model');
      if (present[name]) continue;
      present[name] = true;
      panel.appendChild(items[j]);
    }
  }

  function loadPage(page) {
    var url =
      window.Shopify.routes.root +
      'collections/' +
      encodeURIComponent(handle) +
      '?section_id=ts-rider-overflow&page=' +
      page;

    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('overflow ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var lists = parsed.querySelectorAll('[data-tsp-overflow-panel]');
        for (var i = 0; i < lists.length; i++) {
          mergeInto(
            lists[i].getAttribute('data-tsp-overflow-panel'),
            lists[i].querySelectorAll('[data-tsp-model]')
          );
        }
      });
  }

  /* The tab counts were rendered from the products Liquid could see, so they
     understate once the rest are merged in. Restated from a template carried on
     the element, because plural forms and word order differ per market. */
  function restateCounts() {
    for (var i = 0; i < tabs.length; i++) {
      var index = tabs[i].getAttribute('data-tsp-tab');
      var panel = root.querySelector('[data-tsp-panel="' + index + '"]');
      var label = tabs[i].querySelector('[data-tsp-count]');
      if (!panel || !label) continue;
      var template = label.getAttribute('data-tsp-count-template');
      if (!template) continue;
      label.textContent = template.replace(
        '__n__',
        String(panel.querySelectorAll('[data-tsp-model]').length)
      );
    }
  }

  if (handle && total > seen) {
    var pages = Math.ceil(total / seen);
    var waiting = [];
    for (var page = 2; page <= pages; page++) {
      waiting.push(
        loadPage(page).catch(function () {
          /* leave the picker with what Liquid could see */
        })
      );
    }
    Promise.all(waiting).then(restateCounts);
  }
})();
